import React, { useEffect, lazy, Suspense } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Context
import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// Pages
import Gallery from "@/pages/Gallery";
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const PortfolioPage = lazy(() => import("@/pages/PortfolioPage"));

// Components
import Navbar from "@/components/Navbar";
import BrowseGate from "@/components/BrowseGate";
import SmoothScroll from "@/components/SmoothScroll";

// Public route - gallery is accessible without login
const ProtectedRoute = ({ children }) => {
  return children;
};

// Admin route - requires admin user
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span role="status">Loading account…</span>
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public route - redirects to gallery if already logged in
const PublicRoute = ({ children, enabled = true }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span role="status">Loading account…</span>
      </div>
    );
  }
  
  if (!enabled || user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

class PageLoadBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <div role="alert" className="min-h-screen px-6 py-32 text-center"><h1 className="text-4xl mb-4">This page could not load</h1><p className="mb-6">Check your connection and reload to try again.</p><button className="filter-pill" onClick={() => window.location.reload()}>Reload page</button></div>;
    return this.props.children;
  }
}

function AppContent() {
  const { loginEnabled, registerEnabled } = useAuth();
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }, [pathname]);
  useEffect(() => {
    document.documentElement.dataset.focusVisible = 'false';
    const pointer = () => {
      document.documentElement.dataset.input = 'pointer';
      document.documentElement.dataset.focusVisible = 'false';
    };
    const keyboard = (event) => {
      document.documentElement.dataset.input = 'keyboard';
      if (event.key === 'Tab') document.documentElement.dataset.focusVisible = 'true';
      else if (event.key === 'Escape') document.documentElement.dataset.focusVisible = 'false';
    };
    window.addEventListener('pointerdown', pointer, true);
    window.addEventListener('keydown', keyboard, true);
    return () => {
      window.removeEventListener('pointerdown', pointer, true);
      window.removeEventListener('keydown', keyboard, true);
    };
  }, []);
  return (
    <div className="min-h-screen transition-theme">
      {/* One site-wide fixed snow background + soft contrast tint */}
      <div className="site-bg" aria-hidden="true" />
      <div className="site-bg-tint tint-light" aria-hidden="true" />
      <div className="site-bg-tint tint-dark" aria-hidden="true" />
      <a href="#main-content" className="skip-link">Skip to content</a>
      <SmoothScroll>
      <BrowseGate>
        <Navbar />
      <main id="main-content" tabIndex={-1}>
      <PageLoadBoundary key={pathname}>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><span role="status">Loading page…</span></div>}>
      <Routes>
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Gallery />
            </ProtectedRoute>
          } 
        />
        <Route
  path="/about"
  element={<About />}
/>
        <Route
          path="/contact"
          element={<Contact />}
        />
        <Route
          path="/login"
          element={
            <PublicRoute enabled={loginEnabled}>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute enabled={registerEnabled}>
              <Register />
            </PublicRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } 
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/photos/:photoId" element={<PortfolioPage />} />
        <Route path="/collections/:albumId" element={<PortfolioPage />} />
        <Route path="*" element={<div className="min-h-screen px-6 py-32 text-center"><h1 className="text-4xl mb-4">Page not found</h1><p className="mb-6">This page may have moved.</p><a href="/" className="underline">Return to the gallery</a></div>} />
      </Routes>
      </Suspense>
      </PageLoadBoundary>
      </main>
      <Toaster position="bottom-right" />
      </BrowseGate>
      </SmoothScroll>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
